import { useEffect, useState } from "react";
import { LifeBuoy, X, Send, ShieldCheck } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

// ============================================================================
// Support Vault Drawer — glassmorphic slide-out help-desk. Available to all
// authenticated tiers. Posts to /api/v1/support/create-ticket which captures
// an isolated read-only snapshot of the caller's account state so admins
// have full context inside the founder-team panel.
// ============================================================================
const CATEGORY_OPTIONS = [
  { value: "billing",       label: "Billing" },
  { value: "feature_guide", label: "Feature Guide" },
  { value: "bug_report",    label: "Bug Report" },
  { value: "other",         label: "Other" },
];

export function SupportVaultDrawer({ open, onClose }) {
  const [category, setCategory] = useState("billing");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [ticketId, setTicketId] = useState(null);

  useEffect(() => {
    if (!open) {
      // Reset a moment after closing so the success state persists visually
      const t = setTimeout(() => { setTicketId(null); setMessage(""); }, 300);
      return () => clearTimeout(t);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape" && !submitting) onClose?.(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, submitting, onClose]);

  const submit = async () => {
    if (submitting) return;
    if (message.trim().length < 5) {
      toast.error("Give us a little more detail — 5+ characters helps us route your ticket.");
      return;
    }
    setSubmitting(true);
    const toastId = toast.loading("Sealing ticket into the Support Vault…");
    try {
      const { data } = await api.post("/v1/support/create-ticket", {
        category,
        message: message.trim(),
        include_snapshot: true,
      });
      setTicketId(data?.ticket_id || null);
      toast.success("Ticket sealed · founder team notified", {
        id: toastId,
        description: `Ref ${data?.ticket_id || "issued"} · status: open`,
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not seal your ticket — try again", { id: toastId });
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div
      data-testid="support-vault-drawer-root"
      className="fixed inset-0 z-[9950]"
      aria-hidden={!open}
    >
      {/* Backdrop */}
      <div
        data-testid="support-vault-backdrop"
        onClick={() => { if (!submitting) onClose?.(); }}
        className="absolute inset-0"
        style={{ background: "rgba(2,6,23,0.7)", backdropFilter: "blur(6px)" }}
      />
      {/* Drawer panel — right slide-out */}
      <aside
        data-testid="support-vault-drawer"
        className="absolute top-0 right-0 h-full w-full sm:w-[440px] overflow-y-auto p-6 support-vault-slide-in"
        style={{
          background: "linear-gradient(180deg, rgba(15,23,42,0.98), rgba(6,25,55,0.98))",
          borderLeft: "1px solid rgba(6,182,212,0.35)",
          boxShadow: "-30px 0 60px -20px rgba(6,182,212,0.35)",
        }}
      >
        <div className="flex items-start justify-between">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.28em] text-cyan-300 flex items-center gap-1.5">
              <LifeBuoy size={12} /> Help Desk · Support Vault
            </div>
            <h3 className="text-white text-2xl font-bold mt-1 tracking-tight">
              How can we help?
            </h3>
            <p className="text-slate-400 text-sm mt-2">
              Every ticket ships with a snapshot of your account state so our founder team can resolve it fast.
            </p>
          </div>
          <button
            onClick={onClose}
            disabled={submitting}
            data-testid="support-vault-close-btn"
            className="text-slate-400 hover:text-white p-2 rounded-lg hover:bg-white/5 transition-colors"
            aria-label="Close support vault"
          >
            <X size={18} />
          </button>
        </div>

        {ticketId ? (
          <div
            data-testid="support-vault-success"
            className="mt-6 rounded-xl p-5 border border-emerald-500/40 bg-emerald-500/8"
          >
            <div className="flex items-center gap-2 text-emerald-300 text-[11px] font-mono uppercase tracking-widest">
              <ShieldCheck size={12} /> Ticket sealed
            </div>
            <div className="mt-2 text-white font-semibold">Reference · <span className="font-mono text-emerald-300">{ticketId}</span></div>
            <p className="text-slate-300 text-sm mt-2">
              Our executive founder team reviews every open ticket. You'll hear back at your registered email — usually within 4 business hours.
            </p>
            <button
              onClick={onClose}
              data-testid="support-vault-done-btn"
              className="mt-4 h-10 px-4 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-950 text-xs font-bold uppercase tracking-widest"
            >
              Done
            </button>
          </div>
        ) : (
          <div className="mt-6 space-y-4">
            <div>
              <label className="text-[10px] font-mono uppercase tracking-[0.24em] text-cyan-300 mb-2 block">
                Category
              </label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                data-testid="support-vault-category-select"
                className="w-full rounded-lg bg-slate-900/70 border border-cyan-500/25 focus:border-cyan-400 outline-none text-white text-sm p-2.5 font-sans"
              >
                {CATEGORY_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[10px] font-mono uppercase tracking-[0.24em] text-cyan-300 mb-2 block">
                Message
              </label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={7}
                maxLength={4000}
                data-testid="support-vault-message-input"
                placeholder="Tell us what's happening — steps to reproduce, expected vs actual, screenshots welcome later."
                className="w-full rounded-lg bg-slate-900/70 border border-cyan-500/25 focus:border-cyan-400 outline-none text-white text-sm p-3 font-sans leading-relaxed"
              />
              <div className="text-[10px] text-slate-500 font-mono mt-1 text-right">
                {message.length} / 4000
              </div>
            </div>
            <div className="rounded-md border border-cyan-500/20 bg-cyan-500/5 px-3 py-2 text-[11px] text-cyan-200/80 font-mono flex items-start gap-2">
              <ShieldCheck size={12} className="mt-0.5 shrink-0" />
              <span>An isolated snapshot of your tier, receipt count, and current page is attached automatically. No card numbers, no receipt bodies.</span>
            </div>
            <button
              onClick={submit}
              disabled={submitting}
              data-testid="support-vault-submit-btn"
              className={`btn-neon w-full h-12 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-bold flex items-center justify-center gap-2 ${submitting ? "opacity-70 cursor-wait" : ""}`}
            >
              {submitting ? "Sealing…" : (<><Send size={14} /> Submit request</>)}
            </button>
          </div>
        )}
      </aside>
      <style>{`
        .support-vault-slide-in {
          animation: support-vault-in 320ms cubic-bezier(0.22,1,0.36,1);
        }
        @keyframes support-vault-in {
          0%   { transform: translateX(24px); opacity: 0; }
          100% { transform: translateX(0);    opacity: 1; }
        }
      `}</style>
    </div>
  );
}

export default SupportVaultDrawer;
